<script setup lang="ts">
/**
 * @file Visual connector between a button and its popover/dropdown.
 * Creates a smooth curved shape using clip-path.
 * Supports three directions for positioning relative to the button.
 */
import { computed } from 'vue'

const { dir = 'end' } = defineProps<{
  fillColor: string
  /**
   * Direction the joint extends toward.
   * - 'end': Joint extends toward the inline-end (right in LTR). Used for TopBar horizontal popovers.
   * - 'bottom': Joint extends downward. Used for TopBar vertical popovers (e.g. user menu).
   * - 'top': Joint extends upward. Used for Field Trip popovers above buttons.
   */
  dir?: 'end' | 'bottom' | 'top'
}>()

const positionClasses = computed(() => {
  switch (dir) {
    case 'end':
      return 'absolute -end-2 top-1/2 -translate-y-1/2'
    case 'bottom':
      return 'absolute -bottom-[9px] start-1/2 -translate-s-1/2 [--tw-rotate:90deg]'
    case 'top':
      return 'absolute -top-[9px] start-1/2 -translate-s-1/2 [--tw-rotate:-90deg]'
    default:
      return ''
  }
})

const clipPathValue = computed(() => {
  return 'path("M8 0L8 10C8 10 6.5 7 4 7C1.5 7 9.93398e-07 10 9.93398e-07 10L1.43051e-06 -3.49691e-07C1.43051e-06 -3.49691e-07 1.5 3 4 3C6.5 3 8 0 8 0Z")'
})
</script>

<template>
  <div
    :class="[positionClasses, 'backdrop-blur-xs']"
    :style="{
      width: '8px',
      height: '10px',
      backgroundColor: fillColor,
      clipPath: clipPathValue,
    }"
  />
</template>
